import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Scale, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Scale className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Terms of Service</h1>
              <p className="text-gray-600">Rules for using Memara</p>
            </div>
          </div>

          <p className="text-sm text-gray-500">Last updated: April 6, 2026</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12 space-y-8">
        <Card>
          <CardHeader>
            <CardTitle>Agreement to Terms</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none">
            <p>
              These Terms of Service (&quot;Terms&quot;) govern your access to and use of Memara,
              a product of EcoImpact Ventures Inc. (&quot;Memara,&quot; &quot;we,&quot; &quot;us,&quot;
              or &quot;our&quot;). By creating an account, accessing the service, or using our APIs,
              SDKs, or integrations, you agree to these Terms. If you do not agree, do not use the
              service.
            </p>
            <p className="mt-4">
              Our{' '}
              <Link to="/legal/privacy-policy" className="text-indigo-600 hover:underline">
                Privacy Policy
              </Link>{' '}
              explains how we handle personal data and is incorporated by reference.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Eligibility &amp; Accounts</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none space-y-4">
            <p>
              You must be able to form a binding contract in your jurisdiction. You are responsible
              for safeguarding your credentials and for all activity under your account. Notify us
              promptly at{' '}
              <a href="mailto:support@memara.io" className="text-indigo-600 hover:underline">
                support@memara.io
              </a>{' '}
              if you suspect unauthorized access.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>The Service</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none space-y-4">
            <p>
              Memara provides hosted tools to store, organize, and retrieve memory and context for AI
              workflows, including APIs, web applications, and integrations. We may modify,
              suspend, or discontinue features with reasonable notice when practicable.
            </p>
            <p>
              Service levels, usage limits, and plan features are described on our website and in
              your subscription (if any). We do not guarantee uninterrupted or error-free operation.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Your Content</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none space-y-4">
            <p>
              You retain rights to content you submit (&quot;Customer Content&quot;). You grant Memara
              a non-exclusive license to host, process, transmit, and display Customer Content only
              as needed to provide and improve the service, enforce these Terms, and comply with law.
            </p>
            <p>
              You represent that you have the rights necessary to submit Customer Content and that
              its processing under these Terms does not violate third-party rights or applicable law.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Acceptable Use</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none">
            <p className="mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Violate law or infringe others&apos; rights</li>
              <li>Upload malware, attempt unauthorized access, or disrupt the service</li>
              <li>Scrape, overload, or probe the service beyond normal use or documented limits</li>
              <li>Use the service to build competing offerings through misuse of our proprietary assets</li>
              <li>Resell or redistribute the service without our written agreement</li>
            </ul>
            <p className="mt-4">
              We may suspend or terminate access for conduct that we reasonably believe violates these
              Terms or creates risk for users or the service.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Fees &amp; Billing</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none space-y-4">
            <p>
              Paid plans are billed according to the terms presented at purchase. Taxes may apply.
              Unless stated otherwise, fees are non-refundable. Downgrades or cancellations take
              effect as described in the billing flow or your plan documentation.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Intellectual Property</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none space-y-4">
            <p>
              Memara and its licensors own the service, software, branding, and documentation,
              subject to the licenses expressly granted in these Terms. No rights are granted except
              as stated here.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Disclaimer</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none">
            <p>
              THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE.&quot; TO THE MAXIMUM
              EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR
              STATUTORY, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
              NON-INFRINGEMENT.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Limitation of Liability</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none space-y-4">
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, MEMARA AND ITS AFFILIATES WILL NOT BE LIABLE
              FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, OR LOSS OF
              PROFITS, DATA, OR GOODWILL, ARISING FROM YOUR USE OF THE SERVICE.
            </p>
            <p>
              OUR AGGREGATE LIABILITY FOR CLAIMS RELATING TO THE SERVICE IN ANY TWELVE-MONTH PERIOD
              WILL NOT EXCEED THE GREATER OF (A) THE AMOUNTS YOU PAID US FOR THE SERVICE IN THAT
              PERIOD OR (B) ONE HUNDRED U.S. DOLLARS (US$100), EXCEPT WHERE LIMITATION IS PROHIBITED
              BY LAW.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Indemnity</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none">
            <p>
              You will defend and indemnify Memara and its affiliates against third-party claims and
              costs arising from your Customer Content, your use of the service in violation of these
              Terms, or your violation of law.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Termination</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none space-y-4">
            <p>
              You may stop using the service at any time. We may suspend or terminate access for
              breach, risk, legal requirements, or non-payment. Provisions that by nature should
              survive (including ownership, disclaimers, limitation of liability, and indemnity) will
              survive termination.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Governing Law</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none">
            <p>
              These Terms are governed by the laws of the State of Maryland, USA, excluding conflict
              of law rules. Courts in Maryland will have exclusive jurisdiction, except where
              prohibited by law.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-green-500" />
              Contact
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none">
            <p className="mb-4">Questions about these Terms:</p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="space-y-2">
                <p className="text-sm">
                  <strong>EcoImpact Ventures Inc. (Memara)</strong>
                  <br />
                  6811 Kenilworth Ave, Suite 500
                  <br />
                  Riverdale, MD 20737
                  <br />
                  United States
                </p>
                <p className="text-sm">
                  <strong>Email:</strong>{' '}
                  <a href="mailto:hello@memara.io" className="text-indigo-600 hover:underline">
                    hello@memara.io
                  </a>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
